import React from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import type { GetServerSideProps } from 'next';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { fetchCompanyReviews } from '@/lib/backend/companyReviews';
import type { CompanyReviewsResponse } from '@/utils/hooks/reviews/types';
import settings from '@/settings';
import i18n from '@/settings/i18n';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import { getShortenCountryName } from '@/lib/use-origin-country';
import Hero from '@/components/homepage/hero';
import SubHero from '@/components/v2/homepage/SubHero';
import MarketplaceV2 from '@/components/v2/common/Marketplace';
import FeaturedClient from '@/components/homepage/FeaturedClient';
import DealsPlaceholder from '@/components/v2/carousels/Deals/Placeholder';
import CategoriesPlaceholder from '@/components/v2/carousels/Categories/Placeholder';

const t = i18n.init('homepage');

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

const PopularCategories = dynamic(
  () => import('@/components/commons/PopularCategories'),
  {
    ssr: true,
  },
);
const Categories = dynamic(
  () => import('@/components/v2/carousels/Categories'),
  {
    ssr: false,
    loading: () => <CategoriesPlaceholder />,
  },
);

const RecentlyViewed = dynamic(
  () => import('@/components/homepage/RecentlyViewed'),
  {
    ssr: false,
  },
);

const Deals = dynamic(() => import('@/components/v2/carousels/Deals'), {
  ssr: false,
  loading: () => <DealsPlaceholder />,
});

const CustomerFavorites = dynamic(
  () => import('@/components/carousels/CustomerFavorites'),
  { ssr: true },
);

// RecentlyViewed renders client-only (its list lives in localStorage), so the
// server can't reserve its space. This runs during HTML parsing, before first
// paint, and flags <html> when the section is going to render (same >1 rule as
// RecentlyViewed) so the slot below can hold its height and nothing shifts when
// the cards mount.
const RECENTLY_VIEWED_FLAG_SCRIPT = `try{var l=JSON.parse(localStorage.getItem('recentlyViewed')||'[]');if(Array.isArray(l)&&l.length>1){document.documentElement.setAttribute('data-recently-viewed','')}}catch(e){}`;

export const getServerSideProps: GetServerSideProps<{
  companyReviews: CompanyReviewsResponse | null;
}> = async () => {
  const companyReviews = await fetchCompanyReviews().catch(() => null);

  return { props: { companyReviews } };
};

export default function Home() {
  return (
    <>
      <WebsiteSchema
        // Naming the market keeps each storefront's title its own. Four English
        // domains previously shared this title byte for byte, which is the sort
        // of duplication Google resolves by discarding the title and showing the
        // bare domain instead.
        title={t`Certified Refurbished Tech for Less on Reebelo ${{
          country: getShortenCountryName(),
        }}`}
        description={t`Empower yourself with your favorite tech from Reebelo. Enjoy up to 70% off high-quality iPhones, laptops, iPads and more. ${{
          freeReturnDays,
        }} day free returns. 1 year warranty.`}
      />
      <Head>
        {/* Stop the page from automatically zooming in on mobile when interacting with input fields */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      {/* Home Top Banner Section */}
      <Hero />
      <SubHero />
      <div className="bg-white">
        <div className="reebelo-container">
          <script
            // Static string, no user input.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: RECENTLY_VIEWED_FLAG_SCRIPT }}
          />
          {/* 365px = section py-8 (64) + heading (28) + mt-2 (8) + card min-h (265) */}
          <div className="[html[data-recently-viewed]_&]:min-h-[365px]">
            <RecentlyViewed />
          </div>
        </div>
        {/* Popular Categories */}
        <PopularCategories isHomePage />
      </div>
      <CustomerFavorites />
      <Deals />
      <div className="bg-white">
        <MarketplaceV2 />
      </div>
      <Categories />
      <div className="bg-white py-8 xxs:py-10 md:py-12 lg:py-14">
        <div className="reebelo-container">
          <FeaturedClient />
        </div>
      </div>
    </>
  );
}
